'use client';

import React, { useState } from 'react';
import { Settings, ArrowRight, ArrowLeft, SkipForward, Globe, Clock, Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useTheme } from '@/lib/theme-context';
import { getBackgroundClasses, getTextClasses, getBorderClasses } from '@/lib/theme-utils';

interface OrganizationSettingsProps {
  settings: {
    timezone: string;
    defaultLanguage: string;
    notificationPreferences: {
      emailAlerts: boolean;
      smsAlerts: boolean;
    };
  };
  onComplete: (settings: any) => void;
  onBack: () => void;
  onSkip: () => void;
  isLoading?: boolean;
}

export function OrganizationSettings({ 
  settings, 
  onComplete, 
  onBack, 
  onSkip, 
  isLoading 
}: OrganizationSettingsProps) {
  const { theme } = useTheme();
  const [currentSettings, setCurrentSettings] = useState(settings);

  const handleTimezoneChange = (timezone: string) => {
    setCurrentSettings(prev => ({ ...prev, timezone }));
  };

  const handleLanguageChange = (language: string) => {
    setCurrentSettings(prev => ({ ...prev, defaultLanguage: language }));
  };

  const handleEmailAlertsChange = (enabled: boolean) => {
    setCurrentSettings(prev => ({
      ...prev,
      notificationPreferences: {
        ...prev.notificationPreferences,
        emailAlerts: enabled
      }
    }));
  };

  const handleSmsAlertsChange = (enabled: boolean) => {
    setCurrentSettings(prev => ({
      ...prev,
      notificationPreferences: {
        ...prev.notificationPreferences,
        smsAlerts: enabled
      }
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onComplete(currentSettings);
  };

  const timezones = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST)' },
    { value: 'UTC', label: 'UTC' },
    { value: 'Europe/London', label: 'London (GMT)' },
    { value: 'Europe/Paris', label: 'Paris (CET)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
    { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
    { value: 'Australia/Sydney', label: 'Sydney (AEST)' }
  ];

  const languages = [
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Español' },
    { value: 'fr', label: 'Français' },
    { value: 'de', label: 'Deutsch' },
    { value: 'it', label: 'Italiano' },
    { value: 'pt', label: 'Português' },
    { value: 'ja', label: '日本語' },
    { value: 'ko', label: '한국어' },
    { value: 'zh', label: '中文' }
  ];

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-6">
        <div className="mx-auto w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mb-3">
          <Settings className="w-6 h-6 text-white" />
        </div>
        <h2 className={`text-xl font-bold ${getTextClasses(theme)} mb-1`}>
          Configure Organization Settings
        </h2>
        <p className={`${getTextClasses(theme, 'muted')} text-sm`}>
          Set up timezone, language, and notification preferences for your organization
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Timezone */}
        <div className={`${getBackgroundClasses(theme, 'card')} ${getBorderClasses(theme)} rounded-lg p-4`}>
          <div className="flex items-center space-x-2 mb-3">
            <Clock className="w-4 h-4 " />
            <h3 className={`text-sm font-semibold ${getTextClasses(theme)}`}>Timezone</h3>
          </div>
          <div className="space-y-1">
            <Label htmlFor="timezone" className={`${getTextClasses(theme)} text-xs`}>
              Default Timezone
            </Label>
            <Select value={currentSettings.timezone} onValueChange={handleTimezoneChange}>
              <SelectTrigger className={`${getBackgroundClasses(theme, 'card')} ${getBorderClasses(theme)} ${getTextClasses(theme)} h-10`}>
                <SelectValue placeholder="Select timezone" />
              </SelectTrigger>
              <SelectContent className={`${getBackgroundClasses(theme, 'popover')} ${getBorderClasses(theme)}`}>
                {timezones.map((tz) => (
                  <SelectItem key={tz.value} value={tz.value} className={`${getTextClasses(theme)}`}>
                    {tz.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className={`text-xs ${getTextClasses(theme, 'muted')}`}>
              This will be the default timezone for all users in your organization
            </p>
          </div>
        </div>

        {/* Language */}
        <div className={`${getBackgroundClasses(theme, 'card')} ${getBorderClasses(theme)} rounded-lg p-4`}>
          <div className="flex items-center space-x-2 mb-3">
            <Globe className="w-4 h-4" style={{ color: 'var(--theme-info)' }} />
            <h3 className={`text-sm font-semibold ${getTextClasses(theme)}`}>Language</h3>
          </div>
          <div className="space-y-1">
            <Label htmlFor="language" className={`${getTextClasses(theme)} text-xs`}>
              Default Language
            </Label>
            <Select value={currentSettings.defaultLanguage} onValueChange={handleLanguageChange}>
              <SelectTrigger className={`${getBackgroundClasses(theme, 'card')} ${getBorderClasses(theme)} ${getTextClasses(theme)} h-10`}>
                <SelectValue placeholder="Select language" />
              </SelectTrigger>
              <SelectContent className={`${getBackgroundClasses(theme, 'popover')} ${getBorderClasses(theme)}`}>
                {languages.map((lang) => (
                  <SelectItem key={lang.value} value={lang.value} className={`${getTextClasses(theme)}`}>
                    {lang.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className={`text-xs ${getTextClasses(theme, 'muted')}`}>
              This will be the default language for the user interface
            </p>
          </div>
        </div>

        {/* Notification Preferences */}
        <div className={`${getBackgroundClasses(theme, 'card')} ${getBorderClasses(theme)} rounded-lg p-4`}>
          <div className="flex items-center space-x-2 mb-3">
            <Bell className="w-4 h-4 " />
            <h3 className={`text-sm font-semibold ${getTextClasses(theme)}`}>Notification Preferences</h3>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <Label className={`${getTextClasses(theme)} text-xs`}>Email Alerts</Label>
                <p className={`text-xs ${getTextClasses(theme, 'muted')}`}>Receive security alerts via email</p>
              </div>
              <Switch
                checked={currentSettings.notificationPreferences.emailAlerts}
                onCheckedChange={handleEmailAlertsChange}
                className="data-[state=checked]:"
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label className={`${getTextClasses(theme)} text-xs`}>SMS Alerts</Label>
                <p className={`text-xs ${getTextClasses(theme, 'muted')}`}>Receive critical alerts via SMS</p>
              </div>
              <Switch
                checked={currentSettings.notificationPreferences.smsAlerts}
                onCheckedChange={handleSmsAlertsChange}
                className="data-[state=checked]:"
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <Button
            type="button"
            onClick={onBack}
            variant="outline"
            className={`flex-1 ${getBackgroundClasses(theme, 'default')} ${getBorderClasses(theme)} ${getTextClasses(theme)} h-10`}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button
            type="submit"
            disabled={isLoading}
            className="flex-1  hover:from-green-600 hover:to-green-700 text-white h-10"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Saving...
              </>
            ) : (
              <>
                Continue
                <ArrowRight className="ml-2 w-4 h-4" />
              </>
            )}
          </Button>
        </div>

        {/* Skip Option */}
        <div className="text-center">
          <button
            type="button"
            onClick={onSkip}
            className={`inline-flex items-center ${getTextClasses(theme, 'muted')} underline text-xs`}
          >
            <SkipForward className="w-3 h-3 mr-1" />
            Skip settings for now
          </button>
          <p className={`text-xs ${getTextClasses(theme, 'muted')} mt-1`}>
            You can configure these settings later from your organization settings
          </p>
        </div>
      </form>

      {/* Help Text */}
      <div className={`mt-4 p-3 ${getBackgroundClasses(theme, 'card')} ${getBorderClasses(theme)} rounded-lg`}>
        <h4 className={`text-xs font-medium ${getTextClasses(theme)} mb-1`}>About Organization Settings</h4>
        <ul className={`text-xs ${getTextClasses(theme, 'muted')} space-y-0.5`}>
          <li>• These settings apply to all users in your organization</li>
          <li>• Individual users can override these in their personal settings</li>
          <li>• You can modify these settings anytime after setup</li>
        </ul>
      </div>
    </div>
  );
} 